import { Loader2 } from "lucide-react";
import type { OrganizationRole, OrganizationType } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { useConfirm } from "@/lib/confirm";
import { useCustomizationLocked } from "@/lib/use-customization-locked";
import type { DashboardMode } from "@/lib/dashboard-mode";
import { getRoleLabel, papeisAtribuiveis } from "./role-meta";
import { PermissoesDoAcesso } from "./PermissoesDoAcesso";
import { BarraDoAcesso } from "./BarraDoAcesso";
import { useAcessoDoMembro } from "./use-acesso-do-membro";
import { ehDonoOuSocio } from "@bookdental/schemas";

type Membro = {
  id: string;
  role: string;
  customRole?: { id: string; label: string } | null;
  user?: { name?: string | null; email?: string | null };
};

type Props = { membro: Membro; mode: DashboardMode; tipos: OrganizationType[]; aberto: boolean };

/**
 * Tipo de acesso do colaborador e o que ele permite, no próprio diálogo.
 *
 * Trocar o tipo de acesso é de quem gerencia a equipe; mexer nas permissões e criar acesso novo
 * é só do proprietário — as mesmas travas do servidor, só refletidas aqui pra ninguém clicar no
 * que vai ser recusado.
 */
export function AcessoDoMembro({ membro, mode, tipos, aberto }: Props) {
  const confirm = useConfirm();
  const me = trpc.me.current.useQuery();
  const souDono = ehDonoOuSocio(me.data?.activeOrganization?.role);
  const souProprietario = me.data?.activeOrganization?.role === "OWNER";
  const travadoPeloPlano = useCustomizationLocked();
  const acessos = trpc.organization.customRoles.list.useQuery(undefined, { enabled: souDono && aberto });
  const equipe = trpc.team.list.useQuery(undefined, { enabled: aberto });
  const a = useAcessoDoMembro(membro, aberto);

  if (a.ehDono) {
    return (
      <p className="rounded-lg border border-zinc-100 p-3 text-sm text-zinc-600 dark:border-border dark:text-muted-foreground">
        Proprietário — acesso total, sempre.
      </p>
    );
  }
  // Sócio só é mexido pelo proprietário.
  if (membro.role === "PARTNER" && !souProprietario) {
    return (
      <p className="rounded-lg border border-zinc-100 p-3 text-sm text-zinc-600 dark:border-border dark:text-muted-foreground">
        Sócio — acesso total. Só o proprietário altera um sócio.
      </p>
    );
  }

  const personalizados = (acessos.data ?? []) as { id: string; label: string; basedOnRole: string; memberCount: number }[];
  const personalizado = a.escolhido.startsWith("custom:")
    ? personalizados.find((r) => r.id === a.escolhido.slice("custom:".length))
    : undefined;
  const nomeDe = (v: string) =>
    v.startsWith("custom:")
      ? (personalizados.find((r) => `custom:${r.id}` === v)?.label ?? membro.customRole?.label ?? "Acesso personalizado")
      : getRoleLabel(v.slice("role:".length), mode);
  const nomeDoAcesso = nomeDe(a.escolhido);
  const pessoas = personalizado
    ? personalizado.memberCount
    : ((equipe.data ?? []) as any[]).filter((m) => !m.customRole && `role:${m.role}` === a.escolhido).length;
  const papelBase = (personalizado?.basedOnRole ?? a.escolhido.slice("role:".length)) as Exclude<OrganizationRole, "OWNER" | "PARTNER">;

  const aplicar = async () => {
    const ok = await confirm({
      title: "Alterar o acesso?",
      description: (
        <span>
          <strong>{membro.user?.name || membro.user?.email || "Esse membro"}</strong> passa a ser{" "}
          <strong>{nomeDoAcesso}</strong>. As permissões mudam na hora, em toda a organização.
        </span>
      ),
      confirmText: "Alterar acesso",
    });
    if (ok) a.aplicarAcesso();
  };

  return (
    <div className="space-y-3">
      <label className="block">
        <span className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-zinc-400 dark:text-muted-foreground/70">
          Tipo de acesso
        </span>
        <select
          value={a.escolhido}
          onChange={(e) => a.escolher(e.target.value)}
          disabled={a.salvando}
          className="h-10 w-full rounded-md border border-zinc-200 bg-white px-2.5 text-sm text-zinc-800 dark:border-border dark:bg-card dark:text-foreground"
        >
          <optgroup label="Padrão">
            {papeisAtribuiveis(mode, membro.role, souProprietario).map((r) => (
              <option key={r} value={`role:${r}`}>
                {getRoleLabel(r, mode)}
              </option>
            ))}
          </optgroup>
          {souDono && personalizados.length > 0 && (
            <optgroup label="Personalizados">
              {personalizados.map((r) => (
                <option key={r.id} value={`custom:${r.id}`}>
                  {r.label}
                </option>
              ))}
            </optgroup>
          )}
          {!souDono && membro.customRole && (
            <option value={`custom:${membro.customRole.id}`} disabled>
              {membro.customRole.label}
            </option>
          )}
        </select>
      </label>

      {a.acessoTotal ? (
        <p className="rounded-lg bg-zinc-50 p-3 text-[13px] text-zinc-600 dark:bg-muted/40 dark:text-muted-foreground">
          Sócio tem acesso total, como o proprietário — menos transferir a organização e mexer em outro
          sócio. Dar esse papel pede o seu PIN.
        </p>
      ) : a.carregando ? (
        <div className="flex justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-brand-600" />
        </div>
      ) : (
        <PermissoesDoAcesso
          tipos={tipos}
          efetiva={a.efetiva}
          mexidas={a.mexidas}
          onAlternar={a.alternar}
          editavel={souDono}
        />
      )}
      {!souDono && (
        <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">
          Só o proprietário altera as permissões de um acesso.
        </p>
      )}

      <BarraDoAcesso
        nomeDoAcesso={nomeDoAcesso}
        pessoas={pessoas}
        mudouAcesso={a.mudouAcesso}
        mudouPermissoes={a.mudancas.length > 0}
        podeCriar={!travadoPeloPlano}
        motivoSemCriar="Acesso personalizado faz parte dos planos pagos."
        salvando={a.salvando}
        onAplicar={aplicar}
        onAtualizar={a.atualizarAcesso}
        onSalvarComoNovo={(nome) => a.salvarComoNovo(nome, papelBase)}
        onDescartar={a.descartar}
      />
    </div>
  );
}
